import { useEffect, useState } from "react";
import { Maximize2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";

const ProductCard = ({ item, testId, index, onZoom }) => {
  const gallery = item.gallery?.length ? item.gallery : [item.img];
  return (
    <Reveal delay={(index % 4) * 70} className="h-full">
      <article
        data-testid={testId}
        className="flex h-full flex-col overflow-hidden rounded-xl border border-brand-border bg-brand-surface transition duration-300 hover:-translate-y-1 hover:shadow-lg"
      >
        <button
          type="button"
          onClick={() => onZoom({ images: gallery, index: 0, caption: item.name })}
          data-testid={`${testId}-zoom`}
          aria-label={`Увеличи снимката: ${item.name}`}
          className="group relative block w-full cursor-zoom-in overflow-hidden text-left"
        >
          <img
            src={item.img}
            alt={item.alt}
            loading="lazy"
            style={item.pos ? { objectPosition: item.pos } : undefined}
            className="aspect-[4/5] w-full object-cover transition duration-500 group-hover:scale-105"
          />
          <span className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition duration-300 group-hover:opacity-100">
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <h3 className="flex-1 text-sm font-medium leading-snug text-brand-dark">
            {item.name}
          </h3>
          <div className="mt-4 flex items-baseline justify-between gap-2 border-t border-brand-border pt-3">
            <span className="text-xs font-light text-brand-gray">{item.qty}</span>
            <span
              data-testid={`${testId}-price`}
              className="font-serif text-xl font-semibold text-brand-accent md:text-2xl"
            >
              {item.price} €
            </span>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Extras = ({
  id,
  title,
  intro,
  note,
  items,
  testIdPrefix,
  chapter,
  chapterLabel,
  gridClass = "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  alt = false,
}) => {
  const [zoom, setZoom] = useState(null);

  const nav = (dir) =>
    setZoom((z) =>
      z ? { ...z, index: (z.index + dir + z.images.length) % z.images.length } : z
    );

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(null);
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "ArrowLeft") nav(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  });

  return (
    <section
      id={id}
      data-testid={`${id}-section`}
      className={`py-24 md:py-32 ${alt ? "bg-brand-alt" : "bg-brand-bg"}`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          {chapter && (
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-brand-accent md:text-sm">
              <span className="mr-3 font-light text-brand-dark/35">{chapter} /</span>
              {chapterLabel}
            </p>
          )}
          <h2 className="max-w-3xl font-serif text-3xl font-light leading-snug tracking-tight text-brand-dark md:text-4xl">
            {title}
          </h2>
          <div className="mt-5 max-w-2xl space-y-3 text-sm font-light leading-relaxed text-brand-gray md:text-base">
            {intro.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </Reveal>

        <div className={`mt-12 grid grid-cols-2 gap-4 md:mt-16 md:gap-6 ${gridClass}`}>
          {items.map((item, i) => (
            <ProductCard
              key={item.name}
              item={item}
              index={i}
              testId={`${testIdPrefix}-card-${i + 1}`}
              onZoom={setZoom}
            />
          ))}
        </div>

        <Reveal>
          <p className="mt-10 border-l-2 border-brand-accent/50 pl-4 text-xs font-light italic leading-relaxed text-brand-gray md:text-sm">
            {note}
          </p>
        </Reveal>
      </div>

      {zoom && <Lightbox zoom={zoom} onClose={() => setZoom(null)} onNav={nav} />}
    </section>
  );
};
